/**
 * Google Analytics 4 integration for usage tracking
 * Privacy-focused implementation with consent management
 */
import { ENV } from '../config/env';

// Global gtag function
let gtag = null;

/**
 * Initialize Google Analytics 4 if measurement ID is provided
 */
export const initializeAnalytics = () => {
  if (!ENV.GA4_MEASUREMENT_ID) {
    console.log('GA4_MEASUREMENT_ID not provided, skipping analytics initialization');
    return;
  }

  try {
    // Create gtag script
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${ENV.GA4_MEASUREMENT_ID}`;
    document.head.appendChild(script1);

    // Create gtag configuration script
    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${ENV.GA4_MEASUREMENT_ID}', {
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false
      });
    `;
    document.head.appendChild(script2);

    // Set global gtag reference
    gtag = window.gtag || function() { (window.dataLayer = window.dataLayer || []).push(arguments); };
    
    console.log('Google Analytics 4 initialized');
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
  }
};

/**
 * Track custom events with privacy-conscious data handling
 */
export const trackEvent = (action, category, label, value, customParameters = {}) => {
  if (!gtag || !ENV.GA4_MEASUREMENT_ID) {
    return;
  }

  try {
    // Keep SO numbers intact for business intelligence - no hashing in analytics
    gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
      ...customParameters
    });

    console.log(`Analytics event: ${action} - ${category} - ${label}`);
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

/**
 * Track page views (useful for SPA navigation)
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (!gtag || !ENV.GA4_MEASUREMENT_ID) {
    return;
  }

  try {
    gtag('config', ENV.GA4_MEASUREMENT_ID, {
      page_path: pagePath,
      page_title: pageTitle
    });

    console.log(`Analytics page view: ${pagePath}`);
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
};

/**
 * Track timing events (performance monitoring)
 */
export const trackTiming = (name, value, category = 'Performance') => {
  if (!gtag || !ENV.GA4_MEASUREMENT_ID) {
    return;
  }

  try {
    gtag('event', 'timing_complete', {
      name: name,
      value: Math.round(value),
      event_category: category
    });

    console.log(`Analytics timing: ${name} - ${Math.round(value)}ms`);
  } catch (error) {
    console.error('Failed to track timing:', error);
  }
};

/**
 * Pre-defined event tracking functions for common user actions
 */
export const analytics = {
  // SO Search Events
  searchSO: (soNumber, success = true, duration = 0) => {
    trackEvent(
      success ? 'search_success' : 'search_failure',
      'SO_Search',
      soNumber,
      duration,
      { search_term: soNumber || '' }
    );
  },

  // Program Loading Events
  loadProgram: (soNumber, programType, duration = 0) => {
    trackEvent('load_program', 'Program_Management', soNumber, duration, {
      program_type: programType
    });
  },

  loadSessions: (sessionCount, duration = 0) => {
    trackEvent('load_sessions', 'Program_Management', null, duration, {
      session_count: sessionCount
    });
  },

  loadAttendees: (attendeeCount, duration = 0) => {
    trackEvent('load_attendees', 'Program_Management', null, duration, {
      attendee_count: attendeeCount
    });
  },

  // Attendance Tracking Events
  toggleAttendance: (present, sessionType = 'unknown') => {
    trackEvent(
      present ? 'mark_present' : 'mark_absent',
      'Attendance',
      sessionType,
      1
    );
  },

  addAttendee: (success = true) => {
    trackEvent(
      success ? 'attendee_added' : 'attendee_add_failed',
      'Attendee_Management',
      null,
      1
    );
  },

  // Export Events
  exportCSV: (attendeeCount, sessionCount) => {
    trackEvent('export_csv', 'Data_Export', null, attendeeCount, {
      session_count: sessionCount
    });
  },

  // Notes Events
  updateNotes: (noteType = 'session') => {
    trackEvent('update_notes', 'Notes', noteType, 1);
  },

  // Error Events
  apiError: (operation, errorType, statusCode = null) => {
    trackEvent('api_error', 'Errors', operation, 1, {
      error_type: errorType,
      status_code: statusCode,
      timestamp: new Date().toISOString()
    });
  },

  // Feature Usage
  useFeature: (featureName, context = '') => {
    trackEvent('feature_usage', 'Features', featureName, 1, {
      context: context
    });
  }
};

export default analytics;